import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { motion } from 'motion/react';
import { 
  Mail, 
  Lock, 
  User, 
  AlertCircle, 
  Loader2, 
  Zap 
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Logo } from '../components/Logo';
import { SEO, SEO_CONFIGS } from '../components/SEO';

export function Signup() {
  const navigate = useNavigate();
  const { signUp } = useAuth();
  
  // Only essential fields
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Basic password validation (less strict for faster onboarding)
    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    setLoading(true);

    try {
      console.log('🚀 Starting signup process for:', email);
      
      const result = await signUp(email, password, {
        name,
        // Billing info will be collected during checkout
        phone: '',
        companyName: '',
        taxId: '',
        country: '',
        address: '',
        city: '',
        postalCode: '',
      });
      
      console.log('✅ Signup API response received');
      console.log('📧 Email:', email);
      
      // Save email to localStorage as backup
      if (typeof window !== 'undefined') {
        localStorage.setItem('pendingVerificationEmail', email);
        console.log('💾 Email saved to localStorage');
      }
      
      // Navigate to email verification page
      const navigationState = { 
        email: email, 
        verificationCode: result?.verificationCode 
      };
      
      console.log('🧭 Navigating to verify-email with state:', navigationState);
      
      navigate('/verify-email', { state: navigationState });
      
      console.log('✅ Navigation called successfully');
    } catch (err: any) {
      console.error('❌ Signup error:', err);
      setError(err.message || 'Failed to create account. Please try again.');
      setLoading(false); // Only reset loading on error
    }
  };

  return (
    <>
      <SEO {...SEO_CONFIGS.signup} />
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 flex items-center justify-center p-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          {/* Logo & Title */}
          <div className="text-center mb-8">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 200 }}
              className="mb-4"
            >
              <div className="w-48 mx-auto">
                <Logo className="w-full h-auto" />
              </div>
            </motion.div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">Create Account</h1>
            <p className="text-slate-600">Join the Solar Investment Analysis Platform</p>
          </div>

          {/* Signup Card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-2xl border-2 border-slate-200 shadow-xl p-8"
          >
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Error Message */}
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-4 bg-red-50 border-2 border-red-200 rounded-lg flex items-start gap-3"
                >
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{error}</p>
                </motion.div>
              )}

              {/* Personal Information Section */}
              <div>
                <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <User className="w-5 h-5 text-[#4F819F]" />
                  Get Started in Seconds
                </h2>
                
                <div className="space-y-4">
                  {/* Name Field */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                        className="w-full pl-12 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-[#4F819F] focus:border-[#4F819F] transition-all outline-none"
                        placeholder="John Doe"
                      />
                    </div>
                  </div>

                  {/* Email Field */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        className="w-full pl-12 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-[#4F819F] focus:border-[#4F819F] transition-all outline-none"
                        placeholder="your.email@example.com"
                      />
                    </div>
                  </div>

                  {/* Password Field */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Password <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        className="w-full pl-12 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-[#4F819F] focus:border-[#4F819F] transition-all outline-none"
                        placeholder="••••••••"
                      />
                    </div>
                    <p className="mt-2 text-xs text-slate-500">
                      Minimum 6 characters
                    </p>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full px-6 py-3 bg-gradient-to-r from-[#4F819F] to-[#4E7F9D] text-white font-semibold rounded-xl shadow-lg shadow-[#4F819F]/30 hover:shadow-xl hover:shadow-[#4F819F]/40 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Creating account...
                  </>
                ) : (
                  <>
                    <Zap className="w-5 h-5" />
                    Create Account
                  </>
                )}
              </button>

              {/* Terms */}
              <p className="text-xs text-center text-slate-500">
                By creating an account, you agree to our{' '}
                <Link to="/terms-of-service" className="text-[#4F819F] hover:underline">
                  Terms of Service
                </Link>{' '}
                and{' '}
                <Link to="/privacy-policy" className="text-[#4F819F] hover:underline">
                  Privacy Policy
                </Link>
              </p>
            </form>

            {/* Sign In Link */}
            <div className="mt-6 text-center">
              <p className="text-slate-600">
                Already have an account?{' '}
                <Link
                  to="/login"
                  className="text-[#4F819F] font-semibold hover:underline"
                >
                  Sign in
                </Link>
              </p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </>
  );
}